/**
 * Eric 2026-06-25 (iter124 — Session A9) — Lecteur vidéo + barre de
 * contrôles cinematic.
 *
 * Extrait de VideoDetailPage.js (refacto P0). Affiche soit le panneau
 * document texte (isTextDoc=true) soit le lecteur HTML5 vidéo avec
 * contrôles custom (play/pause, scrubber, vitesse, volume, fullscreen, PiP,
 * skip ±10s, raccourcis clavier).
 */
import React, { useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import axios from "axios";
import {
  Play, Pause, SkipBack, SkipForward, Gauge,
  SpeakerHigh, SpeakerSlash, CornersOut, PictureInPicture,
  ArrowClockwise, CircleNotch, FileText, VideoCamera, Waveform, Question,
} from "@phosphor-icons/react";

export const VideoPlayerArea = (props) => {
  const { tr } = useLang();
  // Vincent B702 spotlight (Eric 2026-02-11) — Au hover d'un item de la
  // légende, on met en évidence les marqueurs correspondants sur la
  // progress-bar (opacité + scale) et on estompe les autres. Effet
  // « spotlight » façon Loom pour guider l'œil.
  const [spotlightType, setSpotlightType] = useState(null);
  // Vincent B702 spotlight-click (Eric 2026-02-11) — Filtre PERSISTANT :
  // un clic sur un item de légende ne fait pas que highlight (hover), il
  // FIXE le filtre → tous les marqueurs des autres types disparaissent
  // (opacity-0), et le panneau correspondant s'ouvre à droite. Recliquer
  // sur le même item libère le filtre.
  const [filterType, setFilterType] = useState(null);
  const activeType = filterType || spotlightType; // priorité au clic
  const {
    isTextDoc, isStacked, lang, api, video,
    videoRef, playerContainerRef, progressRef,
    isPlaying, setIsPlaying, isBuffering, setIsBuffering,
    isMuted, volume, playbackSpeed,
    showControls, controlsVisible, setControlsVisible,
    showSpeedMenu, setShowSpeedMenu,
    showVolumeSlider, setShowVolumeSlider,
    setShowShortcutsHelp,
    currentTime, duration, hoverTime, setHoverTime, hoverX,
    videoError, setVideoError,
    videoErrorDetail, setVideoErrorDetail,
    knowledgeMarkers,
    knowledgeUnitsRaw = [],
    onLegendActivate,
    handleVideoError, handleLoadedMetadata, handleTimeUpdate,
    handleProgressClick, handleProgressHover,
    togglePlay, toggleMute, toggleFullscreen, togglePiP,
    skip, changeSpeed, changeVolume,
    getTokenUrl, formatTime,
    isProcessing, isComplete, progress,
    allTranscripts,
    TYPE_COLORS, SPEED_OPTIONS,
  } = props;
  return (
    <div className={`space-y-3 min-w-0 w-full`}>
          {/* Vincent B743 v2 (Eric 2026-02-14) — Le `lg:[width:var(--left-pct)]`
              historique a été DÉPLACÉ sur le wrapper parent `video-left-column`
              dans VideoDetailPage.js (pour contenir aussi les cards Jargon +
              Fireflies EMPILÉES sous le player). Ici on reste `w-full` pour
              remplir 100% du wrapper — sinon le compound était 55% × 55% ≈
              30% du viewport (rapporté par Vincent : « la vidéo ne remplit
              pas toute la largeur de sa portion de fenêtre »). */}
          {isTextDoc ? (
            <div
              className="rounded-lg overflow-hidden bg-white border border-zinc-200 shadow-sm"
              data-testid="text-doc-content-panel"
            >
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={16} weight="duotone" className="text-[#002FA7]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                    {lang === "fr" ? "Contenu du document" : "Document content"}
                  </span>
                </div>
                <span className="text-[10px] text-zinc-400">
                  {(video.content_length || (video.content_text || "").length).toLocaleString()} {lang === "fr" ? "caractères" : "chars"}
                </span>
              </div>
              <div
                className="px-4 py-3 text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap max-h-[70vh] overflow-y-auto font-['Inter']"
                data-testid="text-doc-content-body"
              >
                {video.content_text || allTranscripts?.merged?.full_text || (lang === "fr" ? "Contenu indisponible." : "Content unavailable.")}
              </div>
            </div>
          ) : (
            <>
          {/* Cinematic Video Container */}
          <div
            ref={playerContainerRef}
            className="relative rounded-lg overflow-hidden bg-black group"
            style={{ aspectRatio: "16/9" }}
            onMouseMove={showControls}
            onMouseLeave={() => { if (isPlaying) setControlsVisible(false); }}
            onDoubleClick={toggleFullscreen}
            data-testid="video-container"
          >
            {/* Vincent B746 v2 (Eric 2026-02-15) — Bouton fullscreen persistant
                RETIRÉ. Il faisait doublon avec le bouton `fullscreen-btn` de la
                barre de contrôles (coin bas-droit). On garde uniquement celui
                de la barre + le double-clic sur le player pour éviter la
                confusion visuelle (deux icônes CornersOut sur le même player).
                Historique B746 : ajouté le 2026-02-14 pour accès 1-clic quand
                la barre bas s'auto-cache, mais Vincent a confirmé le 2026-02-15
                qu'un seul suffit. */}
            {/* Bogue 1199 (Éric 2026-06-25) — Le message d'erreur REMPLAÇAIT
                le <video> : l'élément quittait le DOM, donc le navigateur ne
                pouvait plus se rattraper tout seul et « Réessayer » agissait
                sur une référence nulle (`videoRef.current`), d'où l'impasse.
                Le bloc est désormais une COUCHE par-dessus un lecteur toujours
                monté, et il affiche la cause exacte renvoyée par le
                navigateur. */}
            {videoError && videoError !== "retry" && !video.ai_generated ? (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white bg-zinc-950/95 gap-4 px-6 text-center" data-testid="video-fallback">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                  <VideoCamera size={32} className="text-zinc-500" />
                </div>
                <p className="text-sm text-zinc-300 font-medium">{videoError}</p>
                {videoErrorDetail && (
                  <p className="text-xs text-zinc-400 max-w-md" data-testid="video-error-detail">
                    {videoErrorDetail}
                  </p>
                )}
                <div className="flex gap-3">
                  <a
                    href={getTokenUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 bg-[#002FA7] text-white rounded-lg text-sm hover:bg-[#002280] font-medium flex items-center gap-2 transition-colors"
                    data-testid="open-new-tab-btn"
                  >
                    <VideoCamera size={16} /> Ouvrir la vidéo
                  </a>
                  {/* Bogue 1199 — « Pourquoi ? » interroge le serveur : droits,
                      fichier rattaché, cache local, accessibilité du stockage.
                      Sans ça, impossible de savoir si le refus vient du
                      navigateur ou du serveur. */}
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await axios.get(
                          `${api}/api/videos/${video.id}/stream/diagnose`,
                          { withCredentials: true },
                        );
                        if (setVideoErrorDetail) setVideoErrorDetail(data?.verdict || JSON.stringify(data));
                      } catch (err) {
                        if (setVideoErrorDetail) {
                          setVideoErrorDetail(err?.response?.data?.detail || err.message);
                        }
                      }
                    }}
                    className="px-5 py-2.5 border border-zinc-700 text-zinc-300 rounded-lg text-sm hover:bg-zinc-800 flex items-center gap-2 transition-colors"
                    data-testid="diagnose-video-btn"
                  >
                    <Question size={16} /> Pourquoi ?
                  </button>
                  <button
                    onClick={() => {
                      // Vincent XLSX 2026-07-15 Row 490 (Eric 2026-07-16 SPEC STRICK) —
                      // « Réessayer » ne se contentait pas de reset l'état — le <video>
                      // avait déjà abandonné (readyState=0). On force un vrai reload de
                      // la source : cache-bust via ?t=timestamp, puis .load() explicite.
                      setVideoError(null);
                      if (setVideoErrorDetail) setVideoErrorDetail(null);
                      setIsBuffering(true);
                      try {
                        const v = videoRef?.current;
                        if (v) {
                          const src = v.querySelector("source");
                          if (src) {
                            const baseUrl = getTokenUrl();
                            const sep = baseUrl.includes("?") ? "&" : "?";
                            src.src = `${baseUrl}${sep}t=${Date.now()}`;
                          }
                          v.load();
                        }
                      } catch (err) {
                        console.warn("Video retry failed:", err);
                      }
                    }}
                    className="px-5 py-2.5 border border-zinc-700 text-zinc-300 rounded-lg text-sm hover:bg-zinc-800 flex items-center gap-2 transition-colors"
                    data-testid="retry-video-btn"
                  >
                    <ArrowClockwise size={16} /> {tr("Réessayer", "Try again")}
                  </button>
                </div>
              </div>
            ) : null}
            {videoError && videoError !== "retry" && video.ai_generated ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-zinc-950 gap-4">
                <Waveform size={48} className="text-[#002FA7]" />
                <p className="text-sm text-zinc-300 font-medium">
                  {tr("Vidéo IA — narration audio", "AI video — audio narration")}
                </p>
                <audio controls className="w-80" data-testid="ai-audio-player">
                  <source src={`${api}/api/ai-video/generations/${video.generation_id}/audio`} type="audio/mpeg" />
                </audio>
                <p className="text-xs text-zinc-500">
                  {tr("Générée par KNORIA IA à partir d'un PDF", "Generated by KNORIA AI from a PDF")}
                </p>
              </div>
            ) : !getTokenUrl() ? (
              /* Tâche 1210 (Eric 2026-06-26) — On attend le jeton de lecture
                 avant de monter le `<video>`. Un `<source>` dont on change le
                 src après coup n'est PAS rechargé par le navigateur (il faut
                 appeler load()) : monter le lecteur avec une source vide
                 aurait donné un écran noir définitif. */
              <div
                className="absolute inset-0 flex flex-col items-center justify-center text-white bg-zinc-950 gap-3"
                data-testid="video-player-awaiting-token"
              >
                <CircleNotch size={32} className="animate-spin text-zinc-400" />
                <p className="text-xs text-zinc-400">{tr("Préparation de la lecture…", "Preparing playback…")}</p>
              </div>
            ) : (
              <>
                {/* Vincent (2026-07-22) — Ticket 521 : afficher le lecteur
                    vidéo dès l'upload, sans attendre que le pipeline IA soit
                    terminé. Avant, le player était caché par un placeholder
                    "Traitement en cours…" pendant tout le pipeline, ce qui
                    empêchait Vincent de visualiser la vidéo pour vérifier
                    qu'elle avait bien été uploadée. Le player s'affiche
                    maintenant en parallèle des étapes du pipeline (juste au-
                    dessous). */}
                {/* Video Element */}
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain cursor-pointer"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onCanPlay={() => setIsBuffering(false)}
                  onWaiting={() => setIsBuffering(true)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onError={handleVideoError}
                  onClick={togglePlay}
                  preload="metadata"
                  playsInline
                  data-testid="video-player"
                >
                  {/* Vincent XLSX 2026-07-15 Row 490 (Eric 2026-07-16 SPEC STRICK) —
                      Deux <source> pour maximiser la compatibilité navigateur :
                      d'abord sans type (le navigateur sniffe et choisit), puis
                      un fallback en video/mp4 explicite. Résout le cas
                      « 02.3a. Fiches de fonds EQ » et autres fichiers dont le
                      container ffmpeg ne matche pas video/mp4. */}
                  <source src={getTokenUrl()} />
                  <source src={getTokenUrl()} type="video/mp4" />
                </video>

                {/* Buffering Overlay */}
                {isBuffering && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
                    <CircleNotch size={48} className="text-white animate-spin" />
                  </div>
                )}

                {/* Vincent (2026-07-22) — Badge discret "Traitement IA en cours"
                    en overlay haut-droit pendant le pipeline, pour rappeler à
                    l'utilisateur que la vidéo joue mais que l'analyse n'est
                    pas encore terminée (transcription/diarisation à venir). */}
                {isProcessing && (
                  <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full pointer-events-none" data-testid="processing-overlay-badge">
                    <CircleNotch size={12} className="text-[#3b82f6] animate-spin" />
                    <span className="text-[11px] font-medium text-white">Analyse IA en cours…</span>
                  </div>
                )}

                {/* Play Button Overlay (when paused) */}
                {!isPlaying && !isBuffering && (
                  <div
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={togglePlay}
                  >
                    <div className="w-20 h-20 rounded-full bg-[#002FA7]/90 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-blue-500/20 hover:scale-110 transition-transform">
                      <Play size={36} weight="fill" className="text-white ml-1" />
                    </div>
                  </div>
                )}

                {/* Custom Controls Bar */}
                <div
                  className={`absolute bottom-0 left-0 right-0 transition-all duration-300 ${
                    controlsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
                  }`}
                  style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.85))" }}
                  data-testid="video-controls"
                >
                  {/* Progress Bar */}
                  <div
                    ref={progressRef}
                    className="relative h-8 px-4 flex items-end cursor-pointer group/progress"
                    onClick={handleProgressClick}
                    onMouseMove={handleProgressHover}
                    onMouseLeave={() => setHoverTime(null)}
                    data-testid="progress-bar"
                  >
                    {/* Knowledge markers */}
                    {knowledgeMarkers.map((marker, i) => {
                      const tc = TYPE_COLORS[marker.type] || TYPE_COLORS.concept;
                      // Vincent B702 spotlight — Effet visuel au hover/clic
                      // légende. `filterType` (clic) masque totalement les
                      // autres marqueurs ; `spotlightType` (hover) les
                      // estompe seulement.
                      const isTarget = activeType === marker.type;
                      const filterHides = filterType && filterType !== marker.type;
                      const dimmed = !filterType && spotlightType && spotlightType !== marker.type;
                      return (
                        <div
                          key={i}
                          className={`absolute bottom-2 w-1.5 rounded-full z-10 transition-all duration-200 ${
                            filterHides
                              ? "h-3 opacity-0 pointer-events-none"
                              : isTarget && activeType
                              ? "h-4 opacity-100 shadow-[0_0_8px_rgba(255,255,255,0.6)] cursor-pointer"
                              : dimmed
                              ? "h-3 opacity-20"
                              : "h-3 opacity-70 hover:opacity-100 hover:scale-y-125 cursor-pointer"
                          }`}
                          style={{ left: `calc(${marker.pct}% + 16px - 3px)`, backgroundColor: tc.color }}
                          title={marker.title}
                          data-marker-type={marker.type}
                          data-marker-unit-id={marker.id}
                          onClick={(e) => {
                            // Vincent B702 bonus 5 (Eric 2026-02-11) —
                            // Clic sur un marqueur : (1) empêche le seek
                            // par défaut de la progress-bar, (2) déclenche
                            // l'ouverture du panneau + focus sur l'item.
                            e.stopPropagation();
                            if (onLegendActivate && marker.type && marker.id) {
                              // Fait un seek quand même (préserve UX marker click)
                              try {
                                const v = document.querySelector("video");
                                if (v && marker.pct != null && v.duration) {
                                  v.currentTime = (marker.pct / 100) * v.duration;
                                }
                              } catch (_) { /* silencieux */ }
                              onLegendActivate(marker.type, marker.id);
                            }
                          }}
                        />
                      );
                    })}

                    {/* Track */}
                    <div className="w-full h-1 group-hover/progress:h-1.5 rounded-full bg-white/20 transition-all relative mb-2">
                      {/* Buffered */}
                      <div className="absolute top-0 left-0 h-full rounded-full bg-white/10" style={{ width: "100%" }} />
                      {/* Played */}
                      <div
                        className="absolute top-0 left-0 h-full rounded-full transition-all"
                        style={{ width: `${progress}%`, background: "linear-gradient(90deg, #002FA7, #3b82f6)" }}
                      />
                      {/* Thumb */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg shadow-black/30 opacity-0 group-hover/progress:opacity-100 transition-opacity"
                        style={{ left: `calc(${progress}% - 7px)` }}
                      />
                    </div>

                    {/* Hover tooltip */}
                    {hoverTime !== null && (
                      <div
                        className="absolute bottom-8 px-2 py-1 bg-black/90 text-white text-xs rounded font-mono pointer-events-none"
                        style={{ left: `${hoverX}px`, transform: "translateX(-50%)" }}
                      >
                        {formatTime(hoverTime)}
                      </div>
                    )}
                  </div>

                  {/* Controls Row */}
                  <div className="flex items-center gap-1 px-4 pb-3">
                    {/* Left Controls */}
                    <button
                      onClick={togglePlay}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors"
                      data-testid="play-pause-btn"
                      title={isPlaying ? (lang === "fr" ? "Pause (espace)" : "Pause (space)") : (lang === "fr" ? "Lecture (espace)" : "Play (space)")}
                    >
                      {isPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
                    </button>

                    <button
                      onClick={() => skip(-10)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      data-testid="skip-back-btn"
                      title={lang === "fr" ? "Reculer de 10 secondes" : "Skip back 10 seconds"}
                    >
                      <SkipBack size={18} />
                    </button>

                    <button
                      onClick={() => skip(10)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      data-testid="skip-forward-btn"
                      title={lang === "fr" ? "Avancer de 10 secondes" : "Skip forward 10 seconds"}
                    >
                      <SkipForward size={18} />
                    </button>

                    {/* Volume */}
                    <div
                      className="relative flex items-center"
                      onMouseEnter={() => setShowVolumeSlider(true)}
                      onMouseLeave={() => setShowVolumeSlider(false)}
                    >
                      <button
                        onClick={toggleMute}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        data-testid="volume-btn"
                        title={isMuted || volume === 0 ? (lang === "fr" ? "Activer le son" : "Unmute") : (lang === "fr" ? "Couper le son" : "Mute")}
                      >
                        {isMuted || volume === 0 ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
                      </button>
                      <div className={`overflow-hidden transition-all duration-200 ${showVolumeSlider ? "w-20 opacity-100 ml-1" : "w-0 opacity-0"}`}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={isMuted ? 0 : volume}
                          onChange={(e) => changeVolume(parseFloat(e.target.value))}
                          className="w-full h-1 accent-[#002FA7] cursor-pointer"
                          data-testid="volume-slider"
                        />
                      </div>
                    </div>

                    {/* Time */}
                    <div className="text-xs text-white/70 font-mono ml-2" data-testid="time-display">
                      {formatTime(currentTime)} <span className="text-white/40">/</span> {formatTime(duration)}
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Speed */}
                    <div className="relative">
                      <button
                        onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                        className="h-7 px-2 flex items-center gap-1 rounded-md text-xs text-white/70 hover:text-white hover:bg-white/10 transition-colors font-medium"
                        data-testid="speed-btn"
                        title={lang === "fr" ? "Vitesse de lecture" : "Playback speed"}
                      >
                        <Gauge size={14} />
                        {playbackSpeed}x
                      </button>
                      {showSpeedMenu && (
                        <div className="absolute bottom-full right-0 mb-2 bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-lg overflow-hidden shadow-xl min-w-[80px]" data-testid="speed-menu">
                          {SPEED_OPTIONS.map((s) => (
                            <button
                              key={s}
                              onClick={() => changeSpeed(s)}
                              className={`w-full px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors ${
                                playbackSpeed === s ? "text-[#3b82f6] font-semibold" : "text-white/80"
                              }`}
                              data-testid={`speed-option-${s}`}
                            >
                              {s}x
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* PiP */}
                    <button
                      onClick={togglePiP}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      data-testid="pip-btn"
                      title="Picture in Picture"
                    >
                      <PictureInPicture size={18} />
                    </button>

                    {/* Fullscreen */}
                    <button
                      onClick={toggleFullscreen}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      data-testid="fullscreen-btn"
                      title={tr("Plein écran (F)", "Full screen (F)")}
                    >
                      <CornersOut size={18} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Vincent B702 (Eric 2026-02-11) — Légende du code-couleur des
              traits verticaux de la timeline. Affichée sous le lecteur, avec
              uniquement les types EFFECTIVEMENT présents dans la vidéo (on
              ne pollue pas l'UI avec des types absents). Data-testid stable
              pour les tests Playwright. */}
          {!isTextDoc && isComplete && knowledgeUnitsRaw && knowledgeUnitsRaw.length > 0 && (() => {
            // Utilise la liste BRUTE des KUs (indépendante de `duration`, qui
            // n'est disponible qu'après lecture). Legend disponible dès le
            // chargement de la page.
            const presentTypes = Array.from(new Set(knowledgeUnitsRaw.map((u) => u.type).filter(Boolean)));
            if (presentTypes.length === 0) return null;
            const TYPE_LABELS_FR = {
              qa: "Q&R", procedure: "Procédure", script: "Script",
              objection: "Objection", rebuttal: "Réfutation",
              concept: "Concept", synthesis: "Synthèse",
            };
            const TYPE_LABELS_EN = {
              qa: "Q&A", procedure: "Procedure", script: "Script",
              objection: "Objection", rebuttal: "Rebuttal",
              concept: "Concept", synthesis: "Synthesis",
            };
            const labels = lang === "fr" ? TYPE_LABELS_FR : TYPE_LABELS_EN;
            return (
              <div
                className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap pt-1"
                data-testid="timeline-color-legend"
              >
                <span className="uppercase tracking-wider text-[10px] text-zinc-400 font-semibold">
                  {lang === "fr" ? "Repères timeline" : "Timeline markers"}
                </span>
                {presentTypes.map((t) => {
                  const c = (TYPE_COLORS[t] || TYPE_COLORS.concept).color;
                  const count = knowledgeUnitsRaw.filter((u) => u.type === t).length;
                  const isHovered = spotlightType === t && !filterType;
                  const isFiltered = filterType === t;
                  const isDimmed = filterType && filterType !== t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onMouseEnter={() => setSpotlightType(t)}
                      onMouseLeave={() => setSpotlightType(null)}
                      onFocus={() => setSpotlightType(t)}
                      onBlur={() => setSpotlightType(null)}
                      onClick={() => {
                        // Vincent B702 — Toggle du filtre persistant. Un
                        // second clic sur le même type libère le filtre.
                        const next = filterType === t ? null : t;
                        setFilterType(next);
                        if (next && onLegendActivate) onLegendActivate(next);
                      }}
                      className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-all ${
                        isFiltered
                          ? "bg-white ring-2 ring-offset-1 scale-105 shadow-sm"
                          : isHovered
                          ? "bg-zinc-100 scale-105"
                          : isDimmed
                          ? "opacity-40 hover:opacity-80"
                          : "hover:bg-zinc-50"
                      }`}
                      style={isFiltered ? { ["--tw-ring-color"]: c } : undefined}
                      data-testid={`legend-${t}`}
                      data-filtered={isFiltered ? "true" : "false"}
                      aria-label={`${labels[t] || t} — ${count}${isFiltered ? " — filtre actif" : ""}`}
                      aria-pressed={isFiltered}
                      title={
                        isFiltered
                          ? (lang === "fr"
                              ? `Cliquer pour retirer le filtre ${labels[t] || t}`
                              : `Click to clear ${labels[t] || t} filter`)
                          : (lang === "fr"
                              ? `Cliquer pour isoler ${labels[t] || t} (${count})`
                              : `Click to isolate ${labels[t] || t} (${count})`)
                      }
                    >
                      <span
                        className={`inline-block w-1.5 rounded-full transition-all ${
                          isHovered || isFiltered ? "h-4 shadow-[0_0_6px_rgba(0,0,0,0.15)]" : "h-3.5"
                        }`}
                        style={{ backgroundColor: c }}
                        aria-hidden="true"
                      />
                      <span style={{ color: c }} className={isHovered || isFiltered ? "font-bold" : "font-medium"}>
                        {labels[t] || t}
                      </span>
                      <span className="text-zinc-400 tabular-nums">{count}</span>
                    </button>
                  );
                })}
                {filterType && (
                  <button
                    type="button"
                    onClick={() => setFilterType(null)}
                    className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors border border-zinc-200"
                    data-testid="legend-filter-clear"
                    title={lang === "fr" ? "Retirer le filtre" : "Clear filter"}
                  >
                    <span aria-hidden="true">×</span>
                    <span>{lang === "fr" ? "Tout afficher" : "Show all"}</span>
                  </button>
                )}
              </div>
            );
          })()}

          {/* Keyboard Shortcuts Hint */}
          {isComplete && (
            <div className="flex items-center gap-4 text-[11px] text-zinc-400 flex-wrap">
              <span><kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-zinc-500 font-mono text-[10px]">{tr("Espace", "Space")}</kbd> {tr("Lecture / Pause", "Play / Pause")}</span>
              <span><kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-zinc-500 font-mono text-[10px]">←→</kbd> ±10s</span>
              <span><kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-zinc-500 font-mono text-[10px]">F</kbd> {tr("Plein écran", "Full screen")}</span>
              <span><kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-zinc-500 font-mono text-[10px]">M</kbd> {tr("Muet", "Mute")}</span>
              <span><kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded text-zinc-500 font-mono text-[10px]">V</kbd> {tr("Mode empilé", "Stacked view")}</span>
              <button
                type="button"
                onClick={() => setShowShortcutsHelp(true)}
                className="text-[#002FA7] hover:underline font-medium"
                data-testid="open-shortcuts-help-btn"
                title={tr("Voir tous les raccourcis (?)", "See every shortcut (?)")}
              >
                {lang === "fr" ? "Tous les raccourcis (?)" : "All shortcuts (?)"}
              </button>
            </div>
          )}

          {/* Description */}
          {video.description && (
            <div className="bg-white border border-zinc-200 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-1 font-['Outfit']">Description</h3>
              <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap" data-testid="video-description">
                {video.description.split(/(\bhttps?:\/\/[^\s)]+)/g).map((chunk, i) =>
                  /^https?:\/\//.test(chunk) ? (
                    <a
                      key={i}
                      href={chunk}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#002FA7] underline break-all hover:text-[#001f70]"
                      data-testid="description-link"
                    >
                      {chunk}
                    </a>
                  ) : (
                    <span key={i}>{chunk}</span>
                  )
                )}
              </p>
            </div>
          )}
          {/* Vincent #304 — Fin du fragment conditionnel `!isTextDoc` */}
          </>
          )}
    </div>
  );
};

export default VideoPlayerArea;
